import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { fetchUserAttributes, getCurrentUser } from "aws-amplify/auth";
import { Authenticator } from "@aws-amplify/ui-react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Layout from "./components/Layout";
import Usage from "./pages/Usage";
import Impact from "./pages/Impact";
import NotFound from "./pages/NotFound";
import { AppProvider, AppContext } from "./store/AppContext";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { UsageApi } from "./utils/api";
import AuthHeader from "./components/AuthHeader";
import AuthFooter from "./components/AuthFooter";
import AuthBackground from "./components/AuthBackground";
import {
  Box,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";

import "@aws-amplify/ui-react/styles.css";
import "./App.css";
import "./auth-styles.css";
import ExportImpact from "./pages/ExportImpact";
import ExportViewPage from "./pages/ExportViewPage";

// Define custom theme (Vuetify-like colors)
const theme = createTheme({
  palette: {
    primary: {
      main: "#002966", // Vuetify-style blue
    },
    secondary: {
      main: "#FF4081", // Accent color
    },
  },
  typography: {
    button: {
      textTransform: "none", // Removes uppercase styling from buttons
    },
  },
});

// Custom theme for Amplify Authenticator
const amplifyTheme = {
  name: "guardify-theme",
  tokens: {
    colors: {
      brand: {
        primary: {
          10: "#E6EEF5",
          20: "#CCDDEB",
          30: "#99BBE1",
          40: "#6699D7",
          50: "#3377CD",
          60: "#002966", // Main brand color
          70: "#00204D",
          80: "#001833",
          90: "#000D1A",
          100: "#000000",
        },
      },
      font: {
        interactive: "#002966",
        primary: "#333333",
        secondary: "#666666",
      },
      background: {
        primary: "transparent",
        secondary: "#F7F7F7",
        tertiary: "#F0F0F0",
      },
      border: {
        primary: "#CCCCCC",
        focus: "#002966",
        error: "#DC3545",
      },
    },
    components: {
      authenticator: {
        modal: {
          backgroundColor: "transparent",
          boxShadow: "none",
          padding: "0",
        },
        container: {
          maxWidth: "400px",
          margin: "0 auto",
          padding: "20px 0",
        },
      },
      button: {
        primary: {
          backgroundColor: "#002966",
          color: "#FFFFFF",
          borderColor: "#002966",
          borderRadius: "4px",
          padding: "12px 24px",
          fontSize: "16px",
          fontWeight: "500",
          _hover: {
            backgroundColor: "#001833",
          },
          _focus: {
            backgroundColor: "#001833",
            borderColor: "#002966",
          },
        },
        link: {
          color: "#002966",
          fontSize: "14px",
          fontWeight: "500",
          _hover: {
            color: "#001833",
            textDecoration: "underline",
          },
        },
      },
      fieldcontrol: {
        borderColor: "#CCCCCC",
        borderRadius: "4px",
        padding: "12px",
        fontSize: "16px",
        backgroundColor: "#FFFFFF",
        _focus: {
          borderColor: "#002966",
          boxShadow: "0 0 0 2px rgba(0, 41, 102, 0.2)",
        },
      },
      heading: {
        color: "#002966",
        fontSize: "24px",
        fontWeight: "600",
        marginBottom: "16px",
      },
      text: {
        color: "#333333",
        fontSize: "14px",
        lineHeight: "1.5",
      },
      tabs: {
        borderColor: "#CCCCCC",
        color: "#666666",
        activeColor: "#002966",
        activeBorderColor: "#002966",
        padding: "10px 20px",
        fontSize: "14px",
        fontWeight: "500",
      },
    },
  },
};

// AuthenticatedApp component to handle initialization
function AuthenticatedApp({ user, signOut, setSnackbar }) {
  const { state, dispatch } = useContext(AppContext);
  const initialized = useRef(false);
  const isMounted = useRef(true);
  const productName = "";
  // Initialize user details
  const initializeUser = async () => {
    if (!isMounted.current || initialized.current) return;

    try {
      // Get user cohorts
      const response = await UsageApi.getUserCohorts();

      if (
        !response.data?.userCohorts ||
        response.data.userCohorts.length === 0
      ) {
        // No cohorts found - sign out and show error
        await signOut();
        return;
      }

      // Save cohorts to store
      dispatch({
        type: "SET_USER_COHORTS",
        payload: response.data.userCohorts,
      });
      dispatch({ type: "SET_INITIALIZED", payload: true });

      initialized.current = true;
    } catch (error) {
      if (error.status === 404) {
        console.log("404 error");
        setSnackbar({
          open: true,
          message:
            "No cohorts found for user, please contact support@guardify.com",
          severity: "error",
        });
      }

      await signOut();
    }
  };

  // Initialize on mount
  useEffect(() => {
    if (!initialized.current) {
      initializeUser();
    }
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Show loading state while initializing
  if (!state.isInitialized) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography>Loading your data...</Typography>
      </Box>
    );
  }

  return (
    <Layout
      hideNav={true}
      signOut={signOut}
      user={user}
      productName={productName}
    >
      <Routes>
        <Route path="/" element={<Usage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}

function AppContent() {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const redirectHandled = useRef(false);
  const productName = "";

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Handle redirect parameter
  useEffect(() => {
    const handleRedirect = async () => {
      if (redirectHandled.current) return;

      const params = new URLSearchParams(window.location.search);
      const redirectData = params.get("redirect");

      if (redirectData) {
        try {
          redirectHandled.current = true;
          const response = await UsageApi.getAuthRedirect(redirectData);

          // Switch to sign up tab and pre-fill email
          if (response.data?.email) {
            // Wait for the tab to be available
            setTimeout(() => {
              const signUpTab = document.querySelector(
                'button[role="tab"][aria-controls="amplify-id-:r0:-panel-signUp"]'
              );
              console.log("Sign up tab:", signUpTab);
              if (signUpTab) {
                signUpTab.click();
                // Wait for the form to be ready
                setTimeout(() => {
                  const emailInput = document.querySelector(
                    'input[name="email"]'
                  );
                  if (emailInput) {
                    emailInput.value = response.data.email;
                    emailInput.dispatchEvent(
                      new Event("input", { bubbles: true })
                    );
                  }
                }, 100);
              }
            }, 100);
          }

          // Clean up the URL without refreshing the page
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        } catch (error) {
          console.error("Error handling redirect:", error);
          setSnackbar({
            open: true,
            message: "Error processing redirect. Please try again.",
            severity: "error",
          });
        }
      }
    };

    handleRedirect();
  }, []);

  const components = {
    Header: () => <AuthHeader limitedSupport={false} />,
    Footer: () => <AuthFooter />,
  };

  return (
    <ThemeProvider theme={theme}>
      <AuthBackground />
      <Router>
        <Routes>
          <Route path="/public/impact/:id?" element={<Impact />} />
          <Route path="/public/impact/view/:id?" element={<ExportViewPage />} />
          <Route
            path="*"
            element={
              <Authenticator
                class="auth-component"
                loginMechanisms={["email"]}
                theme={amplifyTheme}
                components={components}
              >
                {({ signOut, user }) => (
                  <AuthenticatedApp
                    user={user}
                    signOut={signOut}
                    setSnackbar={setSnackbar}
                  />
                )}
              </Authenticator>
            }
          />
        </Routes>
      </Router>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
}
